import { useCallback, useEffect, useRef, useState } from 'react';

import { CaptionText } from './CaptionText';
import { useImageSizeOverride } from './imageSizeOverrideContext';
import {
  resolveImageSrcForDisplaySize,
  type ImageDisplaySize,
} from './imageUnitSrc';
import { MathText } from './MathText';
import type { HeadingLevel } from './TextUnit';
import type { ImageUnitData } from './useComponent';
import styles from './ImageUnit.module.css';

interface ImageUnitProps {
  headingLevel?: HeadingLevel;
  hideHeading?: boolean;
  unit: ImageUnitData;
}

const DISPLAY_SIZES: Array<{ glyphScale: number; label: string; value: ImageDisplaySize }> = [
  { glyphScale: 0.5, label: 'Small', value: 'small' },
  { glyphScale: 0.75, label: 'Medium', value: 'medium' },
  { glyphScale: 1, label: 'Large', value: 'large' },
];

/**
 * What the size control says it controls.
 *
 * The figure's heading and caption are already announced, so this only has to
 * disambiguate one figure from another on the same page. Preference order is
 * most-specific-and-shortest first; the alt text is a last resort because it
 * describes the image at length rather than naming it.
 */
function getFigureLabel(unit: ImageUnitData): string {
  // `alt` is a string that may be empty (an undescribed image), so it needs ||
  // rather than ?? to fall through to the generic label.
  return (
    unit.numberedHeading ??
    unit.heading ??
    unit.caption ??
    (unit.alt || 'this image')
  );
}

function clampHeadingLevel(level: HeadingLevel | undefined): HeadingLevel {
  if (!level) {
    return 2;
  }

  return Math.min(4, Math.max(2, level)) as HeadingLevel;
}

export function ImageUnit({
  headingLevel,
  hideHeading = false,
  unit,
}: ImageUnitProps) {
  // Effective size resolves authored → course override → reader choice.
  //
  // The reader is LAST on purpose: their control is an accessibility affordance,
  // and an instructor's course-wide preference must not be able to take it away.
  const override = useImageSizeOverride();
  const courseSize: ImageDisplaySize =
    override === 'authored' ? unit.size : override;
  // The size this reader is looking at. Seeded from the COURSE size, not the
  // authored one: under an override of large a reader must find the control
  // already showing large rather than contradicting what is on screen.
  // Deliberately component state and nothing more — it dies with the component,
  // which is what makes the choice per-image and ephemeral.
  const [displaySize, setDisplaySize] = useState<ImageDisplaySize>(courseSize);
  const [hasError, setHasError] = useState(false);
  // Whether the <img> has pixels to paint. An <img> with no intrinsic
  // dimensions is a ZERO-height box until its bytes arrive, so without a
  // reserved box here every image would collapse the placeholder's height the
  // moment it swapped in and expand again on load — two shifts under a reader
  // who is only scrolling. See .imageLoading.
  //
  // One-way false -> true for the life of an element, on purpose: a browser
  // keeps painting the current image while a new src loads, so re-reserving
  // space when the reader picks another size would letterbox an image that is
  // already on screen.
  const [isLoaded, setIsLoaded] = useState(false);
  // Devices with no hover have nothing to reveal the control, and showing it
  // permanently would sit over the figure during ordinary reading. A tap on the
  // image reveals it instead.
  const [isControlRevealed, setIsControlRevealed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(
    typeof IntersectionObserver === 'undefined',
  );
  const wrapperRef = useRef<HTMLElement | null>(null);
  const HeadingTag = `h${clampHeadingLevel(headingLevel)}` as const;
  const visibleHeading = hideHeading
    ? null
    : unit.numberedHeading ?? unit.heading ?? null;
  const sizeClassName =
    displaySize === 'small'
      ? styles.sizeSmall
      : displaySize === 'large'
        ? styles.sizeLarge
        : styles.sizeMedium;
  // Re-derived from the displayed size, never lower than the authored size —
  // see resolveImageSrcForDisplaySize. Without this an enlarged image would be a
  // CSS upscale of the smaller file, which fails the readers most likely to
  // enlarge it.
  const figureLabel = getFigureLabel(unit);
  const src = resolveImageSrcForDisplaySize(
    unit.src,
    unit.resizableSource,
    displaySize,
  );

  useEffect(() => {
    setHasError(false);
    // A different image in this slot starts at ITS authored size rather than
    // inheriting the previous reader's choice.
    setDisplaySize(courseSize);
    setIsControlRevealed(false);
    // courseSize is derived from unit.size and the override, so both are covered
    // by the deps below; listing it directly would re-run on every render where
    // the override object identity changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override, unit.size, unit.src]);

  useEffect(() => {
    if (shouldLoad || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldLoad(true);
        observer.disconnect();
      }
    });

    const currentWrapper = wrapperRef.current;

    if (currentWrapper) {
      observer.observe(currentWrapper);
    }

    return () => {
      observer.disconnect();
    };
  }, [shouldLoad]);

  // onLoad alone is not enough: an image already in the cache can complete
  // before React attaches the handler, and its load event is then long gone —
  // leaving a reserved box over an image that is painted and ready.
  const trackLoadedImage = useCallback((image: HTMLImageElement | null) => {
    if (image?.complete) {
      setIsLoaded(true);
    }
  }, []);

  return (
    <figure className={styles.figure} ref={wrapperRef}>
      {visibleHeading ? (
        <HeadingTag className={styles.heading} id={unit.anchorId}>
          <MathText text={visibleHeading} />
        </HeadingTag>
      ) : null}
      {/* The row spans the content container, so the control sits at a fixed
          top-right position whatever size the image is, and hovering anywhere
          across the band around the image reveals it.

          The click handler is a reveal affordance, not a control: everything it
          reveals is reachable without it — by hover on pointer devices, and by
          Tab on every device, since the control is only visually hidden and so
          stays in the tab order for :focus-within. A real button here would mean
          a permanently visible element over the figure, which is exactly what
          tap-to-reveal exists to avoid. */}
      <div
        className={styles.imageRow}
        data-control-revealed={isControlRevealed ? 'true' : 'false'}
        onClick={() => setIsControlRevealed((revealed) => !revealed)}
      >
        {!hasError ? (
          <div
            aria-label={`Display size for ${figureLabel}`}
            className={styles.sizeControl}
            // Choosing a size must not also toggle the reveal.
            onClick={(event) => event.stopPropagation()}
            role="group"
          >
            {DISPLAY_SIZES.map((option) => (
              <button
                aria-label={option.label}
                aria-pressed={displaySize === option.value}
                className={styles.sizeButton}
                key={option.value}
                onClick={() => setDisplaySize(option.value)}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className={styles.sizeGlyph}
                  style={{
                    height: `${option.glyphScale * 16}px`,
                    width: `${option.glyphScale * 16}px`,
                  }}
                />
              </button>
            ))}
          </div>
        ) : null}
        <div className={`${styles.imageFrame} ${sizeClassName}`}>
          {shouldLoad && !hasError ? (
            <img
              alt={unit.alt}
              className={[
                styles.image,
                sizeClassName,
                isLoaded ? '' : styles.imageLoading,
              ]
                .filter(Boolean)
                .join(' ')}
              loading="lazy"
              onError={() => {
                setHasError(true);
                // This element is about to be replaced by the placeholder, and a
                // retry has to start from a reserved box again.
                setIsLoaded(false);
              }}
              onLoad={() => setIsLoaded(true)}
              ref={trackLoadedImage}
              src={src}
            />
          ) : (
            <div
              aria-hidden="true"
              className={`${styles.placeholder} ${sizeClassName}`}
            />
          )}
        </div>
      </div>
      {unit.caption ? (
        <figcaption className={styles.caption}>
          <CaptionText text={unit.caption} />
        </figcaption>
      ) : null}
      <div aria-live="polite" className={styles.announcement}>
        {displaySize === unit.size ? '' : `Showing ${figureLabel} at ${displaySize} size`}
      </div>
    </figure>
  );
}
